// Kiosk settings store with localStorage persistence
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type SearchBarPosition = 'top' | 'bottom';
export type VoiceMode = 'ptt' | 'full';

/**
 * Vehicle scope for filtering content
 * Hierarchy: All → Make → Make+Model → Project (single vehicle)
 */
export interface VehicleScope {
  type: 'all' | 'make' | 'model' | 'project';
  make?: string;       // e.g., "Ferrari"
  model?: string;      // e.g., "355" (requires make)
  projectId?: string;  // Single vehicle ID (most specific)
  projectName?: string; // Display name for the project
}

interface SettingsStore {
  // Kiosk layout settings
  searchBarPosition: SearchBarPosition;
  setSearchBarPosition: (position: SearchBarPosition) => void;

  // Voice mode - full (always listening) or push-to-talk
  voiceMode: VoiceMode;
  setVoiceMode: (mode: VoiceMode) => void;

  // Offline voice mode - use Sherpa-ONNX WASM instead of Web Speech API
  offlineVoice: boolean;
  setOfflineVoice: (enabled: boolean) => void;

  // Voice follow-up window duration (seconds) - how long to stay in command mode after wake word
  voiceFollowUpDuration: number;
  setVoiceFollowUpDuration: (seconds: number) => void;

  // Vehicle scope - what content to show
  vehicleScope: VehicleScope;
  setVehicleScope: (scope: VehicleScope) => void;

  // Convenience methods for scope changes
  setScopeToAll: () => void;
  setScopeToMake: (make: string) => void;
  setScopeToModel: (make: string, model: string) => void;
  setScopeToProject: (projectId: string, projectName: string, make?: string, model?: string) => void;
}

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      searchBarPosition: 'top',
      setSearchBarPosition: (searchBarPosition) => set({ searchBarPosition }),

      // Default: full voice mode for kiosk (always listening for wake word)
      voiceMode: 'full',
      setVoiceMode: (voiceMode) => set({ voiceMode }),

      // Default: online voice (Web Speech API) - Sherpa-ONNX requires 81MB WASM download
      offlineVoice: false,
      setOfflineVoice: (offlineVoice) => set({ offlineVoice }),

      // Default: 5 seconds follow-up window
      voiceFollowUpDuration: 5,
      setVoiceFollowUpDuration: (voiceFollowUpDuration) => set({ voiceFollowUpDuration }),

      // Default: show all projects
      vehicleScope: { type: 'all' },
      setVehicleScope: (vehicleScope) => set({ vehicleScope }),

      // Convenience setters
      setScopeToAll: () => set({ vehicleScope: { type: 'all' } }),

      setScopeToMake: (make) => set({
        vehicleScope: { type: 'make', make }
      }),

      setScopeToModel: (make, model) => set({
        vehicleScope: { type: 'model', make, model }
      }),

      setScopeToProject: (projectId, projectName, make, model) => set({
        vehicleScope: { type: 'project', projectId, projectName, make, model }
      }),
    }),
    {
      name: 'ventura-machina-settings',
    }
  )
);

/**
 * Helper to get display text for current scope
 */
export function getScopeDisplayText(scope: VehicleScope): string {
  switch (scope.type) {
    case 'all':
      return 'All Projects';
    case 'make':
      return `All ${scope.make}s`;
    case 'model':
      return `All ${scope.make} ${scope.model}s`;
    case 'project':
      return scope.projectName || 'Selected Project';
  }
}

/**
 * Helper to get short display text for header
 */
export function getScopeShortText(scope: VehicleScope): string {
  switch (scope.type) {
    case 'all':
      return 'All';
    case 'make':
      return scope.make || 'Make';
    case 'model':
      return scope.model || 'Model';
    case 'project':
      return scope.projectName || 'Project';
  }
}
